import React, { MouseEventHandler, useMemo } from 'react';
import { formatDate, formatNumber } from '@niaratech/niara-js-commons';
import { useImmutableFunction, useIsMobile } from '@niaratech/niara-react-components';
import { Tooltip } from '@niaratech/niara-react-components/src';
import { differenceInDays } from 'date-fns';
import addDays from 'date-fns/addDays';
import format from 'date-fns/format';
import getDate from 'date-fns/getDate';
import getDay from 'date-fns/getDay';
import isAfter from 'date-fns/isAfter';
import isBefore from 'date-fns/isBefore';
import lastDayOfMonth from 'date-fns/lastDayOfMonth';
import parse from 'date-fns/parse';
import startOfMonth from 'date-fns/startOfMonth';
import * as S from './styles';
import { TooltipProps } from '@mui/material';

// veja o que ocorre em datas no ano 0020 com o parseISO original
const parseISO = v => typeof v == 'string' ? parse(v?.substring(0, 10), 'yyyy-MM-dd', new Date()) : null;
const DAYS_OF_WEEK = (() => {
  const a = [];
  const exampleSunday = new Date(2019, 8, 8);
  for (let i = 0; i < 7; i++) {
    a.push(addDays(exampleSunday, i));
  }
  return a;
})();
type RestrictionsDetailCalendar = {
  childrenAllowed?: boolean;
  closedOnArrival?: boolean;
  closedOnDeparture?: boolean;
  maxAdultCount?: number;
  maxChildAge?: number;
  maxChildCount?: number;
  maxNightCount?: number;
  minAdultCount?: number;
  minChildAge?: number;
  minChildCount?: number;
  minNightCount?: number;
  minTimeBetweenBookingAndCheckin?: number;
  requiredNightCount?: boolean;
};
type DetailsToCalendarItem = {
  available?: boolean;
  currency?: string;
  date?: string;
  ratePlan?: string;
  restrictions?: RestrictionsDetailCalendar;
  units?: number;
  value?: number;
};
type MonthCalendarValue = {
  startDate?: string | null;
  endDate?: string | null;
};
export interface MonthCalendarProps {
  setValue: React.Dispatch<React.SetStateAction<MonthCalendarValue>>;
  value?: MonthCalendarValue;
  min?: string;
  max?: string;
  disabled?: boolean;
  currentMonth: string | Date;
  detailsToCalendar?: DetailsToCalendarItem[];
  onPeriodSet?: {
    (p: {
      value: MonthCalendarValue;
    });
  };
  loading?: boolean;
}
const updateInSelection = (start: string | null, hover: string | null) => {
  if (start && hover) {
    document.querySelectorAll('.niara-month-calendar-button[data-isodate]:not(.in-seleciton)').forEach((element: HTMLButtonElement) => {
      if (element.dataset['isodate'] > start && element.dataset['isodate'] <= hover) {
        element.classList.add('in-selection');
      }
    });
    document.querySelectorAll('.niara-month-calendar-button.in-selection[data-isodate]').forEach((element: HTMLButtonElement) => {
      if (!(element.dataset['isodate'] > start && element.dataset['isodate'] <= hover)) {
        element.classList.remove('in-selection');
      }
    });
  }
};
export const MonthCalendar: React.FC<MonthCalendarProps> = props => {
  const {
    currentMonth: _propCurrentMonth,
    value,
    setValue,
    max: _max,
    min: _min,
    disabled,
    detailsToCalendar,
    onPeriodSet,
    loading
  } = props;
  const min = _min && parseISO(_min);
  const max = _max && parseISO(_max);
  const currentMonth = React.useMemo(() => {
    if (typeof _propCurrentMonth == 'string') {
      return startOfMonth(parseISO(_propCurrentMonth));
    }
    return startOfMonth(_propCurrentMonth);
  }, [_propCurrentMonth]);
  const daysCount = currentMonth && getDate(lastDayOfMonth(currentMonth));
  const days = useMemo(() => {
    const array = [];
    if (daysCount && daysCount <= 31) {
      for (let i = 1; i <= daysCount; i++) {
        array.push(i);
      }
    }
    return array;
  }, [daysCount]);
  const isoToday = format(new Date(), 'yyyy-MM-dd');
  const startDate = value?.startDate;
  const parsedStartDate = startDate ? parseISO(startDate) : null;
  const endDate = value?.endDate;
  const onDayClick: MouseEventHandler<HTMLButtonElement> = useImmutableFunction(e => {
    e.nativeEvent.stopImmediatePropagation();
    e.preventDefault();
    const date = e.currentTarget.dataset['isodate'];
    if (date) {
      setValue(v => {
        const value = v?.startDate == null || v?.endDate != null ? {
          startDate: date
        } : v.startDate > date ? {
          startDate: date,
          endDate: v.startDate
        } : {
          startDate: v.startDate,
          endDate: date
        };
        if (value?.startDate && value?.endDate && onPeriodSet) {
          onPeriodSet({
            value
          });
        }
        return value;
      });
    }
  });
  const onMouseOver: MouseEventHandler<HTMLButtonElement> = useImmutableFunction(e => {
    e.nativeEvent.stopImmediatePropagation();
    e.preventDefault();
    const date = e.currentTarget.dataset['isodate'];
    if (date && startDate && !endDate) {
      updateInSelection(startDate, date);
    }
  });
  const isMobile = useIsMobile();
  const DayWrapper = isMobile ? React.Fragment : Tooltip;
  return <S.MonthCalendarContainer>
      <h2 className="niara-month-calendar-title">{formatDate(currentMonth, undefined, 'MMMM, yyyy')}</h2>
      <div className="niara-month-calendar-grid">
        {DAYS_OF_WEEK.map(d => formatDate(d, undefined, 'EEEEEE')).map(dayOfWeek => <div className="niara-month-calendar-weekday" key={dayOfWeek}>
            {dayOfWeek}
          </div>)}
        {days.map(day => {
        const date = addDays(currentMonth, day - 1);
        const isoDate = format(date, 'yyyy-MM-dd');
        const displayData = detailsToCalendar?.find(item => item.date?.startsWith(isoDate));
        const isToday = isoToday === isoDate;
        const isStart = startDate === isoDate;
        const isEnd = endDate === isoDate;
        const isActive = isStart || isEnd;
        const isInDuration = startDate && endDate && isoDate > startDate && isoDate < endDate;
        const duration = parsedStartDate && isoDate > startDate ? differenceInDays(date, parsedStartDate) : undefined;
        const dayOfWeek = getDay(date);
        const dayDisabled = disabled || min && isBefore(date, min) || max && isAfter(date, max);
        const tooltipTitle = displayData && !displayData.available ? "Sem disponibilidade" : isStart || !startDate || endDate && !isEnd ? "Check-in" : duration > 1 ? duration + " noites" : duration > 0 ? duration + " noite" : "Check-in";
        const restrictionsDescription = !displayData?.restrictions ? null : <ul>
              {displayData.restrictions.closedOnArrival === true && <li>Fechado para Check-in</li>}
              {displayData.restrictions.closedOnDeparture === true && <li>Fechado para Check-out</li>}
              {displayData.restrictions.minNightCount > 0 && <li>Mínimo de diárias {displayData.restrictions.minNightCount}</li>}
              {displayData.restrictions.maxNightCount > 0 && <li>Máximo de diárias {displayData.restrictions.maxNightCount}</li>}
            </ul>;
        const wrapperProps = isMobile ? {} : {
          disableInteractive: true,
          title: <S.Tooltip>
                    {tooltipTitle}
                    {restrictionsDescription}
                  </S.Tooltip>,
          PopperProps: {
            popperOptions: {
              placement: 'top'
            }
          }
        } as TooltipProps;
        return <div className={'niara-month-calendar-cell ' + (day === 1 ? 'first-day-' + dayOfWeek : '')} key={isoDate}>
              {!dayDisabled ? <DayWrapper {...wrapperProps as TooltipProps}>
                  <button className={'niara-month-calendar-button ' + (isToday && 'today ' || '') + (isStart && 'start-date ' || '') + (isEnd && 'end-date ' || '') + (isActive ? 'active ' : '') + (isInDuration ? 'in-selection ' : '') + (displayData && !displayData.available ? 'unavailable ' : '')} type="button" data-isodate={isoDate} tabIndex={-1} disabled={dayDisabled} onClick={onDayClick} onMouseOver={onMouseOver}>
                    {displayData?.restrictions?.minNightCount > 0 && <div className="min-nights">{displayData.restrictions.minNightCount}</div>}
                    {displayData?.available == false && <div className="unavailable-icon">x</div>}

                    <div className="day">{day}</div>
                    {displayData?.value != null && <div className="price">{formatNumber(displayData?.value, undefined, 2)}</div>}
                    {loading && displayData == null && <div className="loading-indicator" />}
                  </button>
                </DayWrapper> : <div className={'niara-month-calendar-button disabled'}>
                  <div className="day">{day}</div>
                </div>}
            </div>;
      })}
      </div>
    </S.MonthCalendarContainer>;
};
export default MonthCalendar;